import {
  Typography,
  Alert,
  FormControl,
  TextField,
  Button,
  Autocomplete,
  LinearProgress,
  Dialog,
  IconButton,
  DialogContent,
  FormHelperText,
  Switch,
  Chip,
  Box,
  InputAdornment,
} from '@mui/material';
import { useContext, useState } from 'react';

import React from 'react';
import MuiPhoneNumber from 'mui-phone-number';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
import axios, { AxiosResponse } from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';

import { API_ENDPOINT } from '../../../../configurations/global.config';
import { CreateLabelFormProps } from './create-label-form.interfaces';
import {
  LabelKeyWithValues,
  LabelValue,
} from '../../../../interfaces/delivery-company/labels';
import MultiTextField from '../../../common/multi-text-field/multi-text-field';
import { UserType } from '../../../../enums/users';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import { isMobile, isTablet } from 'react-device-detect';
import { GlobalPermissions, user_has_global_permission, user_is_admin } from '../../../../utils/permissions';
import { useSnackBar } from '../../../../contexts/snackbar';
import GenericTable from '../../../common/generic-table/generic-table';
import { displayMoney } from '../../../../utils/money';
import UpdateExtraDeliveryCharge from './update-delivery-charge';
import SingleTextFieldWithChip from '../../../common/input-with-chip/input-with-chip';
import { Cancel } from '@mui/icons-material';

function CreateLabelForm({ defaultLabelId }: CreateLabelFormProps) {
  const [labelWithValues, setLabelWithValues] = useState<LabelKeyWithValues>();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');

  const { user, config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [newValueInput, setNewValueInput] = useState('');
  const [
    updateExtraDeliveryChargeForValue,
    setUpdateExtraDeliveryChargeForValue,
  ] = useState<LabelValue | null>(null);

  const canChangeLabel = user_has_global_permission(user, GlobalPermissions.MANAGE_LABELS)
  const canCreateLabel = user_has_global_permission(user, GlobalPermissions.MANAGE_LABELS)
  const canDeleteLabel = user_has_global_permission(user, GlobalPermissions.MANAGE_LABELS)

  React.useEffect(() => {
    const getAndLabels = async () => {
      if (defaultLabelId !== undefined) {
        setIsLoading(true);
        try {
          const response = await axios.get<LabelKeyWithValues>(
            `${API_ENDPOINT}/api/org/labelkeys/${defaultLabelId}`,
            config
          );
          console.log(response);
          setResponse(response);
          setLabelWithValues(response.data);
        } catch (e) {
          setLabelWithValues(undefined);
          if (axios.isAxiosError(e)) {
            setMessage('Failed to load employee  data: ' + e.message);
          } else {
            setMessage('Failed to load employee  data');
          }
          console.log(e);
        }
        setIsLoading(false);
      } else {
        setLabelWithValues({ key: '', id: '', values: [] });
      }
    };
    getAndLabels();
  }, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (user === null) return;
    if (labelWithValues) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Saving Label...');

      const requestData = {
        key: labelWithValues.key,
        values: labelWithValues.values,
      };

      console.log(requestData);

      try {
        let response;

        console.log('Request Data: ');
        console.log(requestData);

        if (defaultLabelId === undefined) {
          // create new
          response = await axios.post(
            `${API_ENDPOINT}/api/org/labelkeys/`,
            requestData,
            config
          );
        } else {
          // edit existing
          const endpoint = `${API_ENDPOINT}/api/org/labelkeys/${labelWithValues.id}/`;
          response = await axios.patch(endpoint, requestData, config);
        }
        setResponse(response);
        console.log(response);

        if (response.status >= 200 && response.status < 300) {
          setMessage('Labels saved successfully');
        }
      } catch (e: any) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to save merchant: ' + e.message);
        } else {
          setMessage('Failed to save merchant');
        }
        console.log(e);
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const deleteLabel = async () => {
    if (labelWithValues) {
      setIsLoading(true);
      setResponse(undefined);
      try {
        const response = await axios.delete(
          `${API_ENDPOINT}/api/org/labelkeys/${labelWithValues.id}/`,
          config
        );
        setResponse(response);
        if (response.status === 204) {
          setLabelWithValues(undefined);
          setMessage('Labels deleted successfully');
        }
      } catch (e: any) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to save merchant: ' + e.message);
        } else {
          setMessage('Failed to save merchant');
        }
        console.log(e);
      }

      setIsLoading(false);
    }
  };

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        {defaultLabelId === undefined ? 'Create Label' : 'Edit Label'}
      </Typography>
      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message !== '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              {message}

              {response && response.status >= 300 && (
                <ErrorsFlattenner rawErrors={response.data} />
              )}
            </Alert>
          )}

          {labelWithValues && (
            <>
              <br></br>
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <TextField
                    autoComplete="off"
                    label="Label Key"
                    type="text"
                    variant="outlined"
                    required
                    disabled={!canChangeLabel}
                    value={labelWithValues.key}
                    onChange={(e) =>
                      setLabelWithValues({
                        ...labelWithValues,
                        key: e.target.value,
                      })
                    }
                  />
                </FormControl>

                <br />
                <br />

                <TextField
                  fullWidth
                  placeholder="Add new option"
                  value={newValueInput}
                  onChange={(e) => setNewValueInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key == 'Enter') {
                      e.stopPropagation();
                      e.preventDefault();
                      if (newValueInput.length == 0) return;
                      if (
                        labelWithValues.values.find(
                          (item) => item.value == newValueInput
                        )
                      ) {
                        showMessage('Label option already exists');
                      } else {
                        setLabelWithValues({
                          ...labelWithValues,
                          values: [
                            {
                              id: '',
                              value: newValueInput,
                              extra_delivery_fee: [],
                            },
                            ...labelWithValues.values,
                          ],
                        });
                      }

                      setNewValueInput('');
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {newValueInput && (
                          <Chip
                            label="Press Enter to add"
                            size="small"
                            color="secondary"
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                <br />

                <GenericTable<LabelValue>
                  sourceView="create-label-form"
                  items={labelWithValues.values}
                  selectedItems={new Map()}
                  isLoading={false}
                  fullWidth
                  columns={[
                    {
                      id: 'label',
                      header: 'Option',
                      display_body: (item) => (
                        <SingleTextFieldWithChip
                          value={item.value}
                          setValue={(v) => {
                            if (v.length == 0) {
                              showMessage("Label option can't be empty", 'error');
                              return;
                            }
                            setLabelWithValues({
                              ...labelWithValues,
                              values: labelWithValues.values.map((old) => {
                                return old.value == item.value
                                  ? { ...item, value: v }
                                  : old;
                              }),
                            });
                          }}
                        />
                      ),
                    },
                    {
                      id: 'extra delivery charge',
                      header: 'Extra Delivery Charge',
                      display_body: (item) => {
                        return (
                          <Box sx={{ display: 'inline-block' }}>
                            {/** We should require COD only if the order is scheduled for RTO */}
                            {
                              <Box
                                onClick={() =>
                                  setUpdateExtraDeliveryChargeForValue(item)
                                }
                                sx={{
                                  display: 'inline-block',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  bgcolor: 'grey.100',
                                  color: 'text.primary',
                                  cursor: 'pointer',
                                  transition: 'background-color 0.2s',
                                  '&:hover': {
                                    bgcolor: 'grey.200',
                                  },
                                }}
                              >
                                {displayMoney(item.extra_delivery_fee)}
                              </Box>
                            }
                          </Box>
                        );
                      },
                    },
                    {
                      id: 'remove',
                      header: 'Remove',
                      display_body: (item) => {
                        return (
                          <IconButton
                            onClick={() =>
                              setLabelWithValues({
                                ...labelWithValues,
                                values: labelWithValues.values.filter(
                                  (val) => val.value != item.value
                                ),
                              })
                            }
                          >
                            {' '}
                            <Cancel color="error" />{' '}
                          </IconButton>
                        );
                      },
                    },
                  ]}
                  getItemUID={(item) => item.value}
                />

                <br />
                <br />
                <div style={{ display: 'flex', gap: 7 }}>
                  <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    type="submit"
                    variant="contained"
                    disabled={
                      (labelWithValues.id !== '' && !canChangeLabel) ||
                      (labelWithValues.id === '' && !canCreateLabel)
                    }
                  >
                    Save
                  </Button>

                  {labelWithValues.id !== '' && (
                    <Button
                      size={(isMobile && !isTablet) ? 'small' : 'medium'}
                      onClick={() => {
                        setConfirmDelete(true);
                      }}
                      variant="outlined"
                      color="error"
                      disabled={!canDeleteLabel}
                    >
                      Delete Label
                    </Button>
                  )}
                </div>
              </form>{' '}
            </>
          )}
        </>
      )}

      {updateExtraDeliveryChargeForValue && labelWithValues && (
        <UpdateExtraDeliveryCharge
          onSubmit={(m) => {
            setLabelWithValues({
              ...labelWithValues,
              values: labelWithValues.values.map((item) =>
                item.value == updateExtraDeliveryChargeForValue.value
                  ? { ...item, extra_delivery_fee: m }
                  : item
              ),
            });
          }}
          labelValue={updateExtraDeliveryChargeForValue}
          onClose={() => setUpdateExtraDeliveryChargeForValue(null)}
        />
      )}

      <ConfirmAction
        message={'Are you sure you want to delete this label?'}
        action={deleteLabel}
        isOpen={confirmDelete}
        isSevereAction={true}
        confirmSevereActionTarget={'delete ' + labelWithValues?.key}
        handleClose={() => setConfirmDelete(false)}
      >
        <div>
          {' '}
          Attention: This action can not be undone
          <Alert severity="info">
            Deleting this label will not delete orders belonging to this branch.{' '}
          </Alert>
        </div>
      </ConfirmAction>
    </>
  );
}

export default CreateLabelForm;
